import React, { createContext, useContext, useReducer } from "react";
import {
  ShowTriageToolbarOptions,
  TriageToolbarButtonConfig,
  TriageToolbarPayload,
} from "~/components/TriageToolbar/types";

const TriageToolbarContext = createContext<
  TriageToolbarContextType | undefined
>(undefined);

interface TriageToolbarContextType {
  visible: boolean;
  buttons: TriageToolbarButtonConfig[];
  customContent: React.ReactNode | null;
  payload: TriageToolbarPayload;
  show: (options: ShowTriageToolbarOptions) => void;
  hide: () => void;
}

interface FooterState {
  visible: boolean;
  buttons: TriageToolbarButtonConfig[];
  customContent: React.ReactNode | null;
  payload: TriageToolbarPayload;
}

const initialFooterState: FooterState = {
  visible: false,
  buttons: [],
  customContent: null,
  payload: {},
};

type FooterAction =
  | { type: "SHOW_FOOTER"; payload: ShowTriageToolbarOptions }
  | { type: "HIDE_FOOTER" };

export function footerReducer(
  state: FooterState,
  action: FooterAction,
): FooterState {
  switch (action.type) {
    case "SHOW_FOOTER":
      return {
        visible: true,
        buttons: action.payload.buttons ?? [],
        customContent: action.payload.content ?? null,
        payload: action.payload.payload ?? {},
      };
    case "HIDE_FOOTER":
      return {
        ...state,
        visible: false,
        buttons: [],
        customContent: null,
        payload: {},
      };
    default:
      return state;
  }
}

export const useTriageToolbar = (): TriageToolbarContextType => {
  const context = useContext(TriageToolbarContext);
  if (!context) {
    throw new Error(
      "useTriageToolbar must be used within a TriageToolbarProvider",
    );
  }
  return context;
};

export const TriageToolbarProvider: React.FC<{
  children: React.ReactNode;
}> = ({ children }) => {
  const [state, dispatch] = useReducer(footerReducer, initialFooterState);

  const show = (options: ShowTriageToolbarOptions) => {
    dispatch({ type: "SHOW_FOOTER", payload: options });
  };

  const hide = () => {
    dispatch({ type: "HIDE_FOOTER" });
  };

  return (
    <TriageToolbarContext.Provider
      value={{
        ...state,
        show,
        hide,
      }}
    >
      {children}
    </TriageToolbarContext.Provider>
  );
};
